/**
 * @fileoverview Live KOL detail route
 * @module routes/sys/mgmt/$segment/live-kols/$kolId
 */

import type { ReactElement } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { AdminGuard } from '@/features/admin';
import { LiveKOLDetailView } from '@/features/admin/pages/LiveKOLDetailView';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { AdminSidebar, type AdminTab } from '@/features/admin/components/AdminSidebar';

/**
 * Route wrapper with admin guard and segment validation.
 *
 * @component
 */
function LiveKOLDetailRoute(): ReactElement {
  const { segment } = Route.useParams();
  const navigate = useNavigate();
  const expectedSegment = import.meta.env.VITE_ADMIN_PATH_SEGMENT;

  // Validate segment matches expected value
  if (!expectedSegment || segment !== expectedSegment) {
    return <Navigate to="/" />;
  }

  const handleTabChange = (tab: AdminTab): void => {
    navigate({
      to: '/sys/mgmt/$segment',
      params: { segment },
      search: { tab },
    });
  };

  return (
    <AdminGuard>
      <SidebarProvider>
        <AdminSidebar activeTab="approved-kols" onTabChange={handleTabChange} />
        <SidebarInset className="bg-white">
          <div className="bg-white min-h-screen">
            <LiveKOLDetailView />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AdminGuard>
  );
}

/**
 * File-based route configuration.
 *
 * Creates route: /sys/mgmt/$segment/live-kols/$kolId
 */
export const Route = createFileRoute('/sys/mgmt/$segment/live-kols/$kolId')({
  component: LiveKOLDetailRoute,
});
